import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import EditModeButton from '../../components/feature/EditModeButton';
import EditableContent from '../../components/feature/EditableContent';
import { useEditableContent } from '../../hooks/useEditableContent';
import { deratisationDefaultContent } from '../../data/defaultContent';

export default function Deratisation() {
  const { content, isLoading, saveContent } = useEditableContent('deratisation', deratisationDefaultContent);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    const adminStatus = localStorage.getItem('r3d_admin_logged_in');
    setIsAdmin(adminStatus === 'true');
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Dératisation Professionnelle | Élimination Rats & Souris | Protection Nuisibles Pro"
        description="Service de dératisation professionnel et rapide. Élimination garantie des rats et souris avec méthodes certifiées. Intervention 24h/24, devis gratuit. Experts agréés en dératisation."
        keywords="dératisation, rats, souris, rongeurs, élimination rats, dératisation professionnelle, dératisation rapide, traitement rats"
        ogType="article"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Dératisation',
          name: 'Service de Dératisation Professionnelle',
          description: 'Élimination professionnelle et garantie des rats et souris. Intervention rapide avec méthodes certifiées et éco-responsables.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'Protection Nuisibles Pro',
            url: import.meta.env.VITE_SITE_URL
          },
          areaServed: {
            '@type': 'Country',
            name: 'France'
          },
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'EUR',
            lowPrice: '120',
            highPrice: '450',
            offerCount: '3'
          }
        }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <img
          src={content.heroImage.url}
          alt={content.heroImage.alt}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4">{content.pageTitle}</h1>
          <p className="text-xl">{content.pageSubtitle}</p>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">{content.descriptionTitle}</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">{content.descriptionText1}</p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">{content.descriptionText2}</p>
              
              <div className="grid gap-4">
                {content.descriptionFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg flex-shrink-0">
                      <i className={`${feature.icon} text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1 text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={content.descriptionImage.url}
                  alt={content.descriptionImage.alt}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">{content.methodsTitle}</h2>
            <p className="text-xl text-gray-600">{content.methodsSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {content.methods.map((method, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-xl mb-6 mx-auto">
                  <i className={`${method.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-center text-gray-900">{method.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed">{method.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{content.methodsInfo.title}</h3>
            <p className="text-lg text-gray-700 leading-relaxed">{content.methodsInfo.content}</p>
          </div>
        </div>
      </section>

      {/* Tarifs Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">{content.tarifsTitle}</h2>
            <p className="text-xl text-gray-600">{content.tarifsSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.tarifs.map((tarif, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-lg p-8 relative ${
                  tarif.isPopular ? 'ring-2 ring-teal-500 transform scale-105' : ''
                }`}
              >
                {tarif.isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white px-6 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                    Le plus populaire
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{tarif.title}</h3>
                  <p className="text-gray-600 mb-4">{tarif.subtitle}</p>
                  <div className="text-4xl font-bold text-teal-500 mb-1">{tarif.price}</div>
                  <p className="text-sm text-gray-500">{tarif.priceSubtitle}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tarif.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/#contact"
                  className="block w-full text-center bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-6 rounded-lg transition-colors whitespace-nowrap"
                >
                  {tarif.buttonText}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">{content.faqTitle}</h2>
            <p className="text-xl text-gray-600">{content.faqSubtitle}</p>
          </div>

          <div className="space-y-4 mb-12">
            {content.faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-bold text-lg text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-between">
                  <span>{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-2xl text-teal-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>

          <div className="text-center bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold mb-2 text-gray-900">{content.faqContactText}</h3>
            <p className="text-gray-600 mb-6">{content.faqContactSubtext}</p>
            <Link
              to="/#contact"
              className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-8 rounded-lg transition-colors whitespace-nowrap"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-deratisation" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">{content.formTitle}</h2>
            <p className="text-xl text-gray-600">{content.formSubtitle}</p>
          </div>

          <form className="bg-white p-8 rounded-xl shadow-lg space-y-6" data-readdy-form id="deratisation-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nom" className="block text-sm font-bold text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="telephone" className="block text-sm font-bold text-gray-700 mb-2">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="ville" className="block text-sm font-bold text-gray-700 mb-2">
                  Ville *
                </label>
                <input
                  type="text"
                  id="ville"
                  name="ville"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="type-local" className="block text-sm font-bold text-gray-700 mb-2">
                Type de local *
              </label>
              <select
                id="type-local"
                name="type-local"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              >
                <option value="">Sélectionnez...</option>
                <option value="Maison">Maison</option>
                <option value="Appartement">Appartement</option>
                <option value="Commerce">Commerce</option>
                <option value="Entreprise">Entreprise</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">
                Décrivez votre problème *
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                placeholder="Décrivez les signes d'infestation, la localisation, depuis quand..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 caractères</p>
            </div>

            <button
              type="submit"
              className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg whitespace-nowrap"
            >
              Envoyer ma demande
            </button>
          </form>
        </div>
      </section>

      <Footer />

      {isAdmin && (
        <>
          <EditModeButton onClick={() => setShowEditModal(true)} />
          {showEditModal && (
            <EditableContent
              content={content}
              onSave={(newContent) => {
                saveContent(newContent);
                setShowEditModal(false);
              }}
              onCancel={() => setShowEditModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
